/**
 * Marktregime-Detector.
 * Berechnet aus SPY (Aktien/ETF) und BTC (Krypto) einen MarketRegime-Snapshot.
 *
 * Regime-Klassifikation:
 * - risk_on_uptrend: Index >5% über MA200, MA200 steigt → Bull
 * - risk_on_consolidation: Index 0-5% über MA200 → Bull, aber müde
 * - transition: -2% bis +2% um MA200 → Übergangsphase
 * - risk_off_correction: -2% bis -10% unter MA200 → Korrektur
 * - risk_off_bear: <-10% unter MA200 → Bär
 *
 * Wir nutzen 26W-Return als Proxy, wenn keine OHLCV-Daten da sind.
 */

import type { MarketPhase } from '@/types';
import type { MarketRegime } from './types';
import type { OhlcvFeatures } from './ohlcv-features';
import type { PriceQuote } from '@/lib/market-data/types';

export function classifyRegime(
  quote: PriceQuote | null,
  ohlcv: OhlcvFeatures | null,
  context: 'stocks' | 'crypto' | 'bonds' | 'metals'
): MarketRegime {
  if (!quote) {
    return {
      phase: 'transition',
      is_supportive: false,
      notes: `${proxyLabel(context)}-Daten nicht verfügbar.`,
    };
  }

  // Bevorzugt MA200-Position aus OHLCV
  if (ohlcv?.pct_above_ma_long !== null && ohlcv?.pct_above_ma_long !== undefined) {
    const pct = ohlcv.pct_above_ma_long;
    const slope = ohlcv.ma_long_slope_weeks ?? 0;
    return classifyByMa200(pct, slope, context);
  }

  // Fallback via 26W-Return
  const ret26 = quote.return_26w_pct;
  if (ret26 !== null) {
    return classifyByReturn(ret26, context);
  }

  return {
    phase: 'transition',
    is_supportive: false,
    notes: `${proxyLabel(context)} — keine Trend-Daten verfügbar.`,
  };
}

function proxyLabel(context: 'stocks' | 'crypto' | 'bonds' | 'metals'): string {
  switch (context) {
    case 'stocks': return 'SPY';
    case 'crypto': return 'BTC';
    case 'bonds': return 'TLT';
    case 'metals': return 'GLD';
  }
}

function classifyByMa200(
  pctAboveMa: number,
  slopeWeeks: number,
  context: 'stocks' | 'crypto' | 'bonds' | 'metals'
): MarketRegime {
  const indexLabel = proxyLabel(context);
  const slopeText = slopeWeeks >= 4
    ? `MA200 steigt seit ${slopeWeeks} Wochen`
    : 'MA200 flach/fallend';

  let phase: MarketPhase;
  let supportive: boolean;
  let note: string;

  if (pctAboveMa > 5 && slopeWeeks >= 4) {
    phase = 'risk_on_uptrend';
    supportive = true;
    note = `${indexLabel} ${pctAboveMa.toFixed(1)}% über MA200 · ${slopeText} · Risk-On.`;
  } else if (pctAboveMa > 0 && pctAboveMa <= 5) {
    phase = 'risk_on_consolidation';
    supportive = true;
    note = `${indexLabel} ${pctAboveMa.toFixed(1)}% über MA200 · ${slopeText} · Risk-On, müde.`;
  } else if (pctAboveMa >= -2 && pctAboveMa <= 2) {
    phase = 'transition';
    supportive = false;
    note = `${indexLabel} um MA200 (${pctAboveMa.toFixed(1)}%) · Übergangsphase, Vorsicht.`;
  } else if (pctAboveMa >= -10) {
    phase = 'risk_off_correction';
    supportive = false;
    note = `${indexLabel} ${pctAboveMa.toFixed(1)}% unter MA200 · Korrektur · Risk-Off.`;
  } else {
    phase = 'risk_off_bear';
    supportive = false;
    note = `${indexLabel} ${pctAboveMa.toFixed(1)}% unter MA200 · Bärenmarkt.`;
  }

  return { phase, is_supportive: supportive, notes: note };
}

function classifyByReturn(ret26: number, context: 'stocks' | 'crypto' | 'bonds' | 'metals'): MarketRegime {
  const indexLabel = proxyLabel(context);

  let phase: MarketPhase;
  let supportive: boolean;
  let note: string;

  if (ret26 > 10) {
    phase = 'risk_on_uptrend';
    supportive = true;
    note = `${indexLabel} 26W-Return ${ret26.toFixed(1)}% (Proxy ohne MA200) · Risk-On.`;
  } else if (ret26 > 0) {
    phase = 'risk_on_consolidation';
    supportive = true;
    note = `${indexLabel} 26W-Return ${ret26.toFixed(1)}% (Proxy) · schwacher Aufwärtstrend.`;
  } else if (ret26 > -10) {
    phase = 'transition';
    supportive = false;
    note = `${indexLabel} 26W-Return ${ret26.toFixed(1)}% (Proxy) · Übergangsphase.`;
  } else {
    phase = 'risk_off_bear';
    supportive = false;
    note = `${indexLabel} 26W-Return ${ret26.toFixed(1)}% (Proxy) · Bärenmarkt.`;
  }

  return { phase, is_supportive: supportive, notes: note };
}
